'use client'

import Avatar from 'app/_components/Avatar'
import { useKeyEvent } from 'app/_components/Editor/KeyEventContext'
import type { ReturnedEmployee } from 'app/(main)/people/employees/actions'
import { cx } from 'lib/cx'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSlate } from 'slate-react'
import { insertMention } from '.'

export default function Suggestions({
  query,
  suggestionsFetcherAction: suggestionsFetcher,
}: {
  query: string
  suggestionsFetcherAction: (query: string) => Promise<ReturnedEmployee[]>
}) {
  const editor = useSlate()
  const [users, setUsers] = useState<ReturnedEmployee[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // biome-ignore lint/correctness/useExhaustiveDependencies: suggestionsFetcher is a stable module-level function that never changes identity
  useEffect(() => {
    async function fetchUsers() {
      const users = await suggestionsFetcher(query)
      setUsers(users)
    }
    fetchUsers()
  }, [query])

  const handleKeyEvent = useCallback(
    (event: KeyboardEvent) => {
      if (!users || !selectedId) return

      const currentIndex = users.findIndex((u) => u.id === selectedId)

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          if (currentIndex < users.length - 1) {
            setSelectedId(users[currentIndex + 1]!.id)
          }
          break
        case 'ArrowUp':
          event.preventDefault()
          if (currentIndex > 0) {
            setSelectedId(users[currentIndex - 1]!.id)
          }
          break
        case 'Tab':
        case 'Enter':
          event.preventDefault()
          const user = users.find((u) => u.id === selectedId)!
          insertMention(editor, {
            name: user.name ?? 'Unknown user',
            targetId: user.id!,
            targetType: 'Person',
          })
          break
      }
    },
    [users, selectedId, editor],
  )

  useKeyEvent(handleKeyEvent)

  useEffect(() => {
    setSelectedId(users?.[0]?.id ?? null)
  }, [users])

  useEffect(() => {
    // This is probably rendered within a scrollable Popover
    // Make sure to scroll to the selected element
    const selectedElement = containerRef.current?.querySelector(
      `[data-id="${selectedId}"]`,
    )
    selectedElement?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [selectedId])

  if (!users) return null

  return (
    <div ref={containerRef}>
      {users.map((user) => (
        <div
          key={user.id}
          data-id={user.id}
          role="option"
          aria-selected={user.id === selectedId}
          onMouseDown={() =>
            insertMention(editor, {
              name: user.name ?? 'Unknown user',
              targetId: user.id!,
              targetType: 'Person',
            })
          }
          tabIndex={0}
          className={cx(
            user.id === selectedId && 'bg-zinc-200',
            'flex min-h-[24px] cursor-pointer items-center gap-2 p-4 font-semibold whitespace-nowrap text-zinc-800 hover:bg-zinc-100',
          )}
        >
          <Avatar employee={user} />
          <span>{user.name}</span>
        </div>
      ))}
    </div>
  )
}
